import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import themeData from 'utils/theme/themeData';

const { typography } = themeData;

export const TableNoResultsWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
`;

export const TableNoResultsText = styled(typography.TitleOneRegularTypography)``;

export const TableWrapper = styled.div`
  flex-grow: 1;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
`;

export const TableGridWrapper = styled.div`
  display: grid;
  padding: 0 24px;
  column-gap: 16px;
  ${({ columns }) => {
    const columnWidth = 10;
    const templateColumns = Array.from({ length: columns }, () => `${columnWidth}fr`).join(' ');

    return css`
      grid-template: 1fr / ${templateColumns};
    `;
  }}
  ${props => props.extendTableStyle || ''};
`;

export const TableDivider = styled.hr`
  width: calc(100% + 48px);
  grid-column: 1 / 5;
  margin: 0 -24px;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.grey003};
`;

export const TableHeader = styled(typography.TitleTwoTypography)`
  margin: 0;
  padding: 15px 0;
  text-align: start;
  color: ${({ theme }) => theme.colors.grey002};
  border-right: ${({ noBorder, theme }) => !noBorder && `1px solid ${theme.colors.grey003}`};
`;

export const TableItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: 68px;
  border-right: ${({ noBorder, theme }) => !noBorder && `1px solid ${theme.colors.grey003}`};
  padding-right: 15px;
  cursor: ${props => (props.isItemClickable ? 'pointer' : 'unset')};
`;

export const TableItemLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.blueMain};
  border-bottom: 1px solid ${({ theme }) => theme.colors.blueMain};
  padding-bottom: 1px;
  cursor: pointer;
`;

export const TableItemText = styled(typography.BodyTextOneRegularTypography)`
  margin: 0;
  color: ${({ theme }) => theme.colors.black};
  text-align: center;
  ${({ rtl }) => rtl && 'direction: rtl'}
  text-decoration: none;
`;

export const TableItemSubText = styled(typography.BodyTextTwoRegularTypography)`
  margin: 0;
  color: ${({ theme }) => theme.colors.grey002};
`;

export const TableItemStatus = styled(typography.BodyTextTwoBoldTypography)`
  padding: 10px 16px;
  text-align: center;
  border-radius: 8px;

  ${({ isActive, theme }) => `
    color: ${isActive ? theme.colors.greenDark : theme.colors.redDark};
    background-color: ${isActive ? theme.colors.greenLight : theme.colors.redLight};
  `};
`;

export const TableItemWithPhoto = styled.div`
  display: flex;
  align-items: center;
`;

export const TableItemPhoto = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 12px;
  object-fit: cover;
  border-radius: 50%;
`;

export const TableIconBtn = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) => theme.colors.grey000};
  border-radius: 8px;
  box-sizing: border-box;
  cursor: pointer;

  &:before {
    content: ${({ icon }) => icon};
    font-family: 'Icomoon';
    color: ${({ theme }) => theme.colors.black};
  }
`;

export const Center = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const animation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const EmptyState = styled.div`
  padding: 50px;
  animation-name: ${animation};
  animation-duration: 2s;
`;

export const Img = styled.img`
  height: ${props => props.height || 'auto'};
  display: block;
  margin: auto;
`;

export const EmptyStateText = styled(typography.LargeTitleTypography)`
  margin: 5px 0 0 0;
  color: ${({ theme }) => theme.colors.black};
  text-align: center;
  text-decoration: none;
`;

export const EmptyStateSubText = styled(typography.MediumTitleTypography)`
  margin: 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey002};
`;

export const Container = styled.div`
  position: relative;
`;

export const ButtonContainer = styled.div`
  background: ${props => (props.isOpened ? '#E6F1FF' : '#F1F4F6')};
  color: ${props => (props.isOpened ? '#0070CD' : '#484848')};
  border-radius: 8px;
  cursor: pointer;
`;

export const DropDownContainer = styled.div`
  display: ${({ isOpened }) => (isOpened ? 'block' : 'none')};
  right: 0;
  left: unset;
  position: absolute;
  z-index: 1000;
  margin-top: 8px;
`;

export const DropDownBtnsContainer = styled.div`
  display: flex;
  flex-direction: column;
  filter: drop-shadow(0px 4px 16px rgba(0, 0, 0, 0.16));
  border-radius: 8px;
  background-color: #fff;
  width: 160px;
  overflow: hidden;
`;

export const EditButton = styled.div`
  display: flex;
  padding: 10px 16px;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  color: ${({ color }) => color};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey100};
  }

  &:before {
    content: ${({ icon }) => icon};
    font-family: 'Icomoon';
    color: ${({ theme, color }) => color || theme.colors.black};
    margin-right: 8px;
  }
`;

export const ShowMoreButton = styled(typography.TitleTwoTypography)`
  display: flex;
  height: 48px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.grey600};
  cursor: pointer;
`;
